import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, LogOut, Shield, Search, Bell, Sparkles } from "lucide-react";
import { LogoMark, Wordmark } from "./Logo";
import { useStore } from "../store";
import { useAuth } from "../context/AuthContext";
import { useSearchPalette } from "../hooks/useSearchPalette";
import { useInboxCount } from "../api/sitroom";
import { InboxPopover } from "./InboxPopover";
import { useInAppUnreadCount } from "../api/logistics";
import { TopbarThemeButton } from "./TopbarThemeButton";
import { ThemeSwitcher } from "./ThemeSwitcher";
import { LanguageSelector } from "./LanguageSelector";
import { TenantTypeChip } from "./TenantTypeChip";
import { InternalDataIndicator } from "./InternalDataIndicator";
import { ViewSwitcher } from "./ViewSwitcher";
import { useT } from "../lib/i18n";

const initials = (name = "") =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase() || "??";

const LivePill = () => {
  const wsStatus = useStore((s) => s.wsStatus);
  const connected = wsStatus === "connected";
  const reconnecting = wsStatus === "reconnecting" || wsStatus === "connecting";
  const c = connected
    ? { dot: "#27ae60", border: "rgba(39,174,96,0.35)", bg: "rgba(39,174,96,0.10)", text: "#4ed084", label: "LIVE" }
    : reconnecting
    ? { dot: "#f3b14a", border: "rgba(243,177,74,0.35)", bg: "rgba(243,177,74,0.08)", text: "#f3b14a", label: "RECONNECTING" }
    : { dot: "#6b7388", border: "rgba(255,255,255,0.10)", bg: "rgba(255,255,255,0.03)", text: "#9198b0", label: "OFFLINE" };
  return (
    <div
      className="flex items-center gap-2 px-2.5 py-1 rounded-full"
      style={{ background: c.bg, border: `1px solid ${c.border}` }}
      data-testid="live-pill"
      data-status={wsStatus}
    >
      <span
        className={connected ? "live-dot inline-block rounded-full" : "inline-block rounded-full"}
        style={{ width: 7, height: 7, background: c.dot }}
      />
      <span
        style={{
          fontFamily: "DM Mono, monospace",
          fontSize: 10,
          color: c.text,
          letterSpacing: "1.5px",
          fontWeight: 500,
        }}
      >
        {c.label}
      </span>
    </div>
  );
};

export const Topbar = () => {
  const toggleSidebar = useStore((s) => s.toggleSidebar);
  const openPanel = useStore((s) => s.openPanel);
  const openBriefing = useStore((s) => s.openBriefing);
  const { user, logout } = useAuth();
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const menuRef = useRef(null);
  const bellRef = useRef(null);
  const palette = useSearchPalette();
  const navigate = useNavigate();
  const { data: count = { unread: 0 } } = useInboxCount();
  const { data: inappUnread = { count: 0 } } = useInAppUnreadCount();
  // Combined unread: mentions/inbox + in-app logistics notifications (iter87 γ-1).
  const unreadCount = (count?.unread || 0) + (inappUnread?.count || 0);
  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
      if (bellRef.current && !bellRef.current.contains(e.target)) setBellOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);
  return (
    <header
      className="fixed top-0 left-0 right-0 z-[300] flex items-center justify-between px-4 sm:px-5"
      style={{
        height: "var(--topbar-h, 56px)",
        background: "#0d0f18",
        borderBottom: "1px solid rgba(255,255,255,0.12)",
      }}
      data-testid="topbar"
    >
      <div className="flex items-center gap-3">
        <button
          className="lg:hidden flex items-center justify-center rounded-md hover:bg-white/5 hamburger-btn"
          style={{ width: 44, height: 44, minWidth: 44, minHeight: 44 }}
          onClick={toggleSidebar}
          aria-label="Toggle navigation"
          data-testid="hamburger-btn"
        >
          <Menu size={26} strokeWidth={2} color="var(--text, #f0f2f8)" />
        </button>
        <LogoMark size={28} />
        <Wordmark />
      </div>
      <div className="flex items-center gap-3">
        {/* ⌘K search trigger (desktop) */}
        <button
          onClick={palette.open}
          className="hidden sm:flex items-center gap-2 px-2.5 py-1.5 rounded-md transition-colors hover:bg-white/[0.04]"
          style={{
            background: "transparent",
            border: "1px solid rgba(255,255,255,0.10)",
            color: "#9aa1b3",
            fontFamily: "DM Mono, monospace",
            fontSize: 10,
            letterSpacing: "0.4px",
            cursor: "pointer",
          }}
          aria-label="Open search"
          data-testid="topbar-search-btn"
        >
          <Search size={12} />
          <span className="hidden md:inline">{t("topbar.search")}</span>
          <kbd
            style={{
              padding: "1px 5px",
              border: "1px solid rgba(255,255,255,0.10)",
              borderRadius: 3,
              color: "#9198b0",
              fontSize: 9,
            }}
          >
            ⌘K
          </kbd>
        </button>
        {/* Mobile search icon-only */}
        <button
          onClick={palette.open}
          className="sm:hidden flex items-center justify-center rounded-md hover:bg-white/5"
          style={{ width: 36, height: 36 }}
          aria-label="Open search"
          data-testid="topbar-search-btn-mobile"
        >
          <Search size={16} color="#f0f2f8" />
        </button>

        <LivePill />

        {/* Phase A — Tenant type chip (Forwarder / Customer / Carrier) */}
        {user && (
          <div className="hidden sm:block">
            <TenantTypeChip tenantType={user.tenant_type} viewTier={user.view_tier} />
          </div>
        )}

        {/* Phase A safety — "Internal data visible" indicator. Pulses when
            internal cost/margin are reachable, muted otherwise. */}
        {user && (
          <div className="hidden sm:block">
            <InternalDataIndicator viewTier={user.view_tier} />
          </div>
        )}

        {/* Phase A — Google-style multi-tenant view switcher (auto-hidden for single-tenant users) */}
        {user && <ViewSwitcher currentTenantId={user.tenant_id} />}

        {/* Iter35 — visible theme cycle button */}
        <TopbarThemeButton />

        {/* Mentions bell */}
        <div className="relative" ref={bellRef}>
          <button
            onClick={() => setBellOpen((v) => !v)}
            className="flex items-center justify-center rounded-md transition-colors hover:bg-white/[0.04] relative"
            style={{
              width: 32,
              height: 32,
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.10)",
              color: unreadCount > 0 ? "#f0f2f8" : "#9aa1b3",
              cursor: "pointer",
            }}
            aria-label="Notifications"
            data-testid="topbar-bell-btn"
            data-unread={unreadCount}
          >
            <Bell size={14} />
            {unreadCount > 0 && (
              <span
                className="absolute pulse-red flex items-center justify-center"
                style={{
                  top: 2,
                  right: 2,
                  minWidth: 14,
                  height: 14,
                  padding: "0 3px",
                  borderRadius: 7,
                  background: "#E24B4A",
                  color: "#fff",
                  fontFamily: "DM Mono, monospace",
                  fontSize: 8,
                  fontWeight: 700,
                  boxShadow: "0 0 6px rgba(226,75,74,0.7)",
                  lineHeight: 1,
                  letterSpacing: 0,
                }}
                data-testid="bell-unread-dot"
                data-count={unreadCount}
              >
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>
          {bellOpen && <InboxPopover onClose={() => setBellOpen(false)} />}
        </div>

        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setOpen((v) => !v)}
            className="flex items-center justify-center rounded-full transition-all hover:brightness-125"
            style={{
              width: 30,
              height: 30,
              background: "rgba(24,95,165,0.5)",
              color: "#cfe1f6",
              fontFamily: "DM Mono, monospace",
              fontSize: 11,
              fontWeight: 500,
              border: "1px solid rgba(24,95,165,0.6)",
              cursor: "pointer",
            }}
            data-testid="user-avatar"
            aria-label="User menu"
          >
            {initials(user?.name || user?.email)}
          </button>
          {open && (
            <div
              className="absolute right-0 mt-2 rounded-md"
              style={{
                width: "max-content",
                minWidth: 220,
                maxWidth: "min(320px, 96vw)",
                background: "#0d0f18",
                border: "1px solid rgba(255,255,255,0.10)",
                boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
              }}
              data-testid="user-menu"
            >
              <div className="px-3 py-2" style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                <div
                  style={{
                    fontFamily: "Syne, sans-serif",
                    fontSize: 13,
                    color: "#f0f2f8",
                    fontWeight: 600,
                  }}
                  data-testid="user-name"
                >
                  {user?.name || "—"}
                </div>
                <div
                  style={{
                    fontFamily: "DM Mono, monospace",
                    fontSize: 10,
                    color: "#9198b0",
                    letterSpacing: "1.2px",
                    textTransform: "uppercase",
                    marginTop: 2,
                  }}
                  data-testid="user-role"
                >
                  {(user?.role || "—").replace(/_/g, " ")}
                </div>
                <div
                  style={{
                    fontFamily: "DM Mono, monospace",
                    fontSize: 10,
                    color: "#6b7388",
                    marginTop: 4,
                  }}
                  data-testid="user-email"
                >
                  {user?.email || "—"}
                </div>
              </div>
              {user?.role === "platform_admin" && (
                <Link
                  to="/admin/users"
                  onClick={() => setOpen(false)}
                  className="w-full flex items-center gap-2 px-3 py-2 transition-colors"
                  style={{
                    background: "transparent",
                    color: "#f3b14a",
                    fontFamily: "DM Mono, monospace",
                    fontSize: 11,
                    letterSpacing: "1.2px",
                    textTransform: "uppercase",
                    cursor: "pointer",
                    textDecoration: "none",
                    borderBottom: "1px solid rgba(255,255,255,0.06)",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(243,177,74,0.08)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  data-testid="user-menu-admin-users"
                >
                  <Shield size={13} />
                  {t("topbar.admin_users")}
                </Link>
              )}
              <button
                onClick={() => {
                  setOpen(false);
                  openBriefing();
                }}
                className="w-full flex items-center gap-2 px-3 py-2 transition-colors"
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#c89adb",
                  fontFamily: "DM Mono, monospace",
                  fontSize: 11,
                  letterSpacing: "1.2px",
                  textTransform: "uppercase",
                  cursor: "pointer",
                  textAlign: "left",
                  borderBottom: "1px solid rgba(255,255,255,0.06)",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(155,89,182,0.10)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                data-testid="user-menu-briefing"
              >
                <Sparkles size={13} />
                {t("topbar.view_briefing")}
              </button>
              {/* Iter34 — three-way theme picker */}
              <div
                className="px-3 py-2 flex flex-col gap-1.5"
                style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}
              >
                <span style={{
                  fontFamily: "DM Mono, monospace", fontSize: 9,
                  color: "#9198b0", letterSpacing: "1.2px", textTransform: "uppercase",
                }}>{t("topbar.theme")}</span>
                <ThemeSwitcher size="sm" showLabels={true} />
              </div>
              {/* Iter36 — UI language picker (Level 1) */}
              <div
                className="px-3 py-2 flex flex-col gap-1.5"
                style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}
                data-testid="user-menu-language-row"
              >
                <span style={{
                  fontFamily: "DM Mono, monospace", fontSize: 9,
                  color: "#9198b0", letterSpacing: "1.2px", textTransform: "uppercase",
                }}>{t("topbar.language")}</span>
                <LanguageSelector hasUser={true} testIdPrefix="user-menu-language" />
              </div>
              <button
                onClick={async () => {
                  setOpen(false);
                  await logout();
                  window.location.href = "/login";
                }}
                className="w-full flex items-center gap-2 px-3 py-2 transition-colors"
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#E24B4A",
                  fontFamily: "DM Mono, monospace",
                  fontSize: 11,
                  letterSpacing: "1.2px",
                  textTransform: "uppercase",
                  cursor: "pointer",
                  textAlign: "left",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(226,75,74,0.10)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                data-testid="logout-btn"
              >
                <LogOut size={13} />
                {t("topbar.sign_out")}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
